/**
 * K31: paid fast travel. A station is discovered by being there (arriving in its zone, riding a seam gate); a warp
 * between discovered stations costs silver by distance and starts a 2-minute cooldown (kept in progress, as every zone
 * is a new World). Refused in combat, while carrying a sack, during a seam ride, dead, or on cooldown. It lands through
 * the World's own zone transfer. Time only from `step(dt)`; no randomness.
 */
import { zoneEnabled } from '../data/enabledZones';
import { FAST_STATIONS, FAST_TRAVEL, stationById, type FastStation } from '../data/fastTravel';
import { SEAM_GATES, type RegionId } from '../data/regions';
import type { PlayerProgress } from '../progress';
import type { ZoneId } from '../data/zones';
import { regionLanding } from './seamGate';
import type { SystemHost, WorldSystem } from './types';

export type FastTravelCommand = { t: 'fastTravel'; to: string };

const d = (ax: number, ay: number, bx: number, by: number): number => Math.hypot(ax - bx, ay - by);

/** Where a region's seam gate leaves it (own end) and where that gate stands on the centre side. */
function gateOf(r: RegionId): { own: [number, number]; centre: [number, number] } | null {
  const g = SEAM_GATES.find((x) => x.b.region === r);
  return g ? { own: [g.b.x, g.b.y], centre: [g.a.x, g.a.y] } : null;
}

/** Walking metres between two stations along regions and gates, or null when either is not on the region map. */
export function walkMetres(a: FastStation, b: FastStation): number | null {
  if (a.x === null || a.y === null || b.x === null || b.y === null || !a.region || !b.region) return null;
  if (a.region === b.region) return d(a.x, a.y, b.x, b.y);
  let m = 0;
  let ax = a.x, ay = a.y;
  if (a.region !== 'centre') {
    const g = gateOf(a.region);
    if (!g) return null;
    m += d(ax, ay, g.own[0], g.own[1]);
    [ax, ay] = g.centre;
  }
  let bx = b.x, by = b.y;
  if (b.region !== 'centre') {
    const g = gateOf(b.region);
    if (!g) return null;
    m += d(bx, by, g.own[0], g.own[1]);
    [bx, by] = g.centre;
  }
  return m + d(ax, ay, bx, by);
}

/** Silver for a warp: by region metres when both are placed, else the flat zone cost. */
export function warpCost(a: FastStation, b: FastStation): number {
  const m = walkMetres(a, b);
  return m === null ? FAST_TRAVEL.flat : Math.round(FAST_TRAVEL.base + FAST_TRAVEL.perMetre * m);
}

/** Seconds of walking a warp would save (estimate). */
export function walkSeconds(a: FastStation, b: FastStation): number {
  const m = walkMetres(a, b);
  return m === null ? FAST_TRAVEL.flatWalk : m / FAST_TRAVEL.walkSpeed;
}

export const stationOfZone = (z: ZoneId): FastStation | undefined => FAST_STATIONS.find((s) => s.id === `z:${z}`);

export const discoveredIds = (p: PlayerProgress): readonly string[] => p.fastTravel?.seen ?? [];

/** The zone a warp to this station lands in, or null when closed. */
export function landingZone(s: FastStation): ZoneId | null {
  const z = s.zone ?? (s.region ? regionLanding(s.region) : null);
  return z && zoneEnabled(z) ? z : null;
}

export interface WarpOption {
  readonly station: FastStation;
  readonly cost: number;
  /** Why it cannot be taken now (Thai), or undefined. */
  readonly why?: string;
}

/** Discovered stations other than `from` with their cost, for the menu and the map (static reasons only: silver). */
export function warpOptions(p: PlayerProgress, from: ZoneId): WarpOption[] {
  const here = stationOfZone(from);
  const seen = new Set(discoveredIds(p));
  const out: WarpOption[] = [];
  for (const s of FAST_STATIONS) {
    if (!seen.has(s.id) || s.id === here?.id) continue;
    const z = landingZone(s);
    if (!z || z === from) continue;
    const cost = here ? warpCost(here, s) : FAST_TRAVEL.flat;
    out.push({ station: s, cost, why: p.gold < cost ? 'เงินไม่พอ' : undefined });
  }
  return out;
}

export class FastTravelSystem implements WorldSystem {
  readonly name = 'fastTravel';
  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    fastTravel: (cmd: { to: string }) => void this.warp(cmd.to),
  };

  /** Cooldown left (s). */
  cooldown(): number {
    return this.h.world.progress.fastTravel?.cd ?? 0;
  }

  discover(id: string): void {
    const pr = this.h.world.progress;
    if (!stationById(id)) return;
    const ft = (pr.fastTravel ??= { seen: [], cd: 0 });
    if (ft.seen.includes(id)) return;
    ft.seen.push(id);
    this.h.notice(`พบสถานีใหม่: ${stationById(id)!.name}`);
  }

  setup(): void {
    this.discover(`z:${this.h.world.map.zone}`);
  }

  /** Why a warp cannot start now (Thai), or null. */
  blocked(): string | null {
    const w = this.h.world;
    const p = w.player;
    if (p.dead) return 'ตายแล้ว';
    if (w.seam.ride) return 'กำลังเดินทางข้ามด่าน';
    if (p.combatT > 0) return 'ยังอยู่ในการต่อสู้';
    if ((p as { carrying?: number | null }).carrying) return 'วางกระสอบก่อน';
    if (this.cooldown() > 0) return `รอวาร์ปอีก ${Math.ceil(this.cooldown())} วินาที`;
    return null;
  }

  private no(text: string): false {
    this.h.notice(text);
    return false;
  }

  warp(id: string): boolean {
    const w = this.h.world;
    const s = stationById(id);
    const why = this.blocked();
    if (why) return this.no(why);
    if (!s || !discoveredIds(w.progress).includes(id)) return this.no('ยังไม่เคยไปถึงสถานีนั้น');
    const to = landingZone(s);
    if (!to || to === w.map.zone) return this.no('ไปที่นั่นไม่ได้');
    const from = stationOfZone(w.map.zone);
    const cost = from ? warpCost(from, s) : FAST_TRAVEL.flat;
    if (w.progress.gold < cost) return this.no('เงินไม่พอ');
    w.progress.gold -= cost;
    w.progress.fastTravel!.cd = FAST_TRAVEL.cooldown;
    Object.assign(this.h.input, { sprint: false, engage: false, talkTo: null });
    (w as unknown as { travel(to: ZoneId, byPortal: boolean): void }).travel(to, false);
    return true;
  }

  step(dt: number): void {
    const w = this.h.world;
    const ft = w.progress.fastTravel;
    if (ft && ft.cd > 0) ft.cd = Math.max(0, ft.cd - dt);
    // A ride begun discovers the end entered from; the far end when it lands (the next World's setup).
    const r = w.seam.ride;
    if (r) {
      this.discover(`g:${r.gate}:${r.from}`);
      this.discover(`g:${r.gate}:${r.from === 'a' ? 'b' : 'a'}`);
    }
  }
}
